import { useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Preserves scroll position for any scrollable container across navigation.
 * Usage:
 *   const listRef = useScrollRestore('sessions-list');
 *   <div ref={listRef} className="overflow-y-auto ...">
 *
 * Saves to sessionStorage so position survives React re-mounts (back nav),
 * but resets on hard refresh (intentional).
 *
 * Implementation notes (React 18/19):
 * - useLayoutEffect saves scroll on unmount because useEffect cleanup fires AFTER
 *   the DOM element is removed (detached → scrollTop=0). useLayoutEffect cleanup
 *   fires synchronously before DOM removal, so scrollTop is valid.
 * - savedScrollRef reads from sessionStorage at hook-construction time so that
 *   React Strict Mode's fake-unmount (which overwrites sessionStorage with 0)
 *   does not corrupt the restore step.
 * - Restore uses a retry loop: the first rAF fires before sessions have loaded
 *   (empty list → maxScroll=0 → scroll clamped to 0). We retry until the list
 *   is tall enough to accept the saved position, up to ~1 second.
 */
export function useScrollRestore(key) {
  const ref = useRef(null);
  const storageKey = `scroll_pos_${key}`;

  // Read saved position ONCE at hook-construction time (before Strict Mode can overwrite it)
  const savedScrollRef = useRef(parseInt(sessionStorage.getItem(storageKey) || '0', 10));

  // SAVE on unmount — useLayoutEffect fires before DOM removal in React 18+
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    return () => {
      // el is still attached here; scrollTop is valid
      sessionStorage.setItem(storageKey, String(el.scrollTop));
    };
  }, [storageKey]);

  // RESTORE on mount — retry until content is scrollable (async data may not be loaded yet)
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const pos = savedScrollRef.current;
    if (pos <= 0) return;

    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 20; // ~1 second of retries at 50ms each

    const tryRestore = () => {
      if (cancelled || !el) return;
      const maxScroll = el.scrollHeight - el.clientHeight;
      if (maxScroll >= pos) {
        el.scrollTop = pos;
      } else if (attempts < MAX_ATTEMPTS) {
        attempts++;
        // Retry after 50ms to let async content (API data) load
        setTimeout(tryRestore, 50);
      }
    };

    requestAnimationFrame(tryRestore);

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  return ref;
}
